import styled from "styled-components/macro";
import { Link } from "react-router-dom";
import { ERROR_COLOR, THEME_COLOR, THEME_COLOR_GRAY } from "../../vars";
import Overlay, { OverlayTrigger } from "../Overlay";
import AutoReplyOverlay from "../AutoReplyOverlay";
import { Menu, MenuLinkItem, MenuSeparator, MenuButtonItem } from "../Menu";
import { useSkipioApi } from "../../api";
import usePlan from "../../hooks/use-plan";
import HelpButton from "../HelpButton";
import { fetchUser } from "../../api/users";
import { useQuery } from "@tanstack/react-query";

export function ProfileOverlay({ trigger }: { trigger: OverlayTrigger }) {
  const skipioApi = useSkipioApi();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const plan = usePlan();
  const showIntegrationsLink = Object.entries(plan.features).some(
    ([key, on]) => on && key.startsWith("integrations__")
  );
  const { percentOfMessagesUsed: percent } = plan;

  const isRed = percent >= 90;

  const logout = async () => {
    await skipioApi.auth.delete();
    window.location.href = `/`;
  };

  return (
    <Overlay
      border
      trigger={trigger}
      position={{
        bottom: () => 8,
        left: ({ width }) => width,
      }}
      content={({ hide }) => (
        <div
          style={{
            width: "285px",
          }}
        >
          <Menu>
            <ProfileHeader>
              <ProfileHeaderImage
                title={`${user.firstName[0]}${user.lastName[0]}`}
              >
                {Boolean(user.avatarImageUrl) && (
                  <div
                    style={{ backgroundImage: `url(${user.avatarImageUrl})` }}
                  />
                )}
              </ProfileHeaderImage>
              <p>
                <strong data-ignore="true">
                  {user.firstName} {user.lastName}
                </strong>
              </p>
              <p data-ignore="true">
                {user.providerPhoneNumbers.length > 0 &&
                  user.providerPhoneNumbers[0].phoneNumber.replace(
                    /^\+\d(\d{3})(\d{3})(\d{4})/,
                    "($1) $2 $3"
                  )}
              </p>
            </ProfileHeader>
            {plan.isAccountOwner && (
              <LimitBanner red={isRed}>
                {100 - percent}% Remaining Messages
              </LimitBanner>
            )}
            <MenuLinkItem as={Link} onClick={() => hide()} to={"/app/profile"}>
              My Profile
            </MenuLinkItem>
            {user.accountOwner && (
              <MenuLinkItem onClick={() => hide()} as={Link} to="/app/plan">
                My Plan {Boolean(isRed) && <Badge>!</Badge>}
              </MenuLinkItem>
            )}
            <MenuSeparator />
            {user.providerPhoneNumbers.length > 0 && (
              <AutoReplyOverlay
                hideProfileOverlay={hide}
                trigger={({ ref, show }) => (
                  <MenuLinkItem ref={ref} onClick={show} href="#" as="a">
                    Auto-Reply
                  </MenuLinkItem>
                )}
              />
            )}
            {plan.features["voice_broadcasts"] ? (
              <MenuLinkItem
                as={Link}
                onClick={() => hide()}
                to={"/app/voicemails"}
              >
                Ringless Voicemail
              </MenuLinkItem>
            ) : (
              <MenuLinkItem
                as={Link}
                onClick={() => hide()}
                to="/app/success-center/post/b31afc0a-c53f-4ffc-b652-85f2e4cdaaff"
              >
                Ringless Voicemail
              </MenuLinkItem>
            )}
            <MenuLinkItem
              as={Link}
              onClick={() => hide()}
              to={"/app/bounce-reports"}
            >
              Bounce Reports
            </MenuLinkItem>

            <MenuLinkItem as={Link} onClick={() => hide()} to={"/app/links"}>
              SkipLinks
            </MenuLinkItem>

            {plan.features.web2txt && (
              <MenuLinkItem as={Link} onClick={() => hide()} to="/app/web2txt">
                Web2Txt
              </MenuLinkItem>
            )}

            {plan.isMultiUserOwner && !plan.features.teams && (
              <MenuLinkItem
                as={Link}
                onClick={() => hide()}
                to="/app/admin/user-accounts"
              >
                {user.account.name} Users
              </MenuLinkItem>
            )}
            {showIntegrationsLink ? (
              <MenuLinkItem
                as={Link}
                onClick={() => hide()}
                to="/app/integrations"
              >
                Integrations
              </MenuLinkItem>
            ) : (
              <MenuLinkItem
                as={Link}
                onClick={() => hide()}
                to="/app/success-center/post/4aabe149-ae1e-497b-b06a-7c48d53d270f"
              >
                Integrations
              </MenuLinkItem>
            )}
            {plan.features["community__cms"] && (
              <MenuLinkItem
                as={Link}
                onClick={() => hide()}
                to="/app/resource-center/cms"
              >
                Community CMS
              </MenuLinkItem>
            )}
            <MenuSeparator />
            <MenuLinkItem as={Link} to="/app/resource-center">
              Resource Center
            </MenuLinkItem>
            <HelpButton
              trigger={({ ref, show }) => (
                <MenuLinkItem ref={ref} onClick={show} href="#" as="a">
                  Contact Us
                </MenuLinkItem>
              )}
            />
            <MenuSeparator />
            <MenuButtonItem onClick={logout}>Logout</MenuButtonItem>
            <MenuSeparator />
            <Legal>
              <span className="copyright">
                © {new Date().getFullYear()} Skipio
              </span>{" "}
              &bull;{" "}
              <a
                target="_blank"
                href="https://www.skipio.com/terms"
                rel="noreferrer"
              >
                Terms of Use
              </a>
            </Legal>
          </Menu>
        </div>
      )}
    />
  );
}

const Legal = styled.div`
  line-height: 3em;
  padding: 0px 20px;
  font-size: 12px;
  &.copyright {
    color: #676163;
  }
  a {
    color: #9598a6;
    transition: color 0.2s ease-out;
    &:hover {
      color: ${THEME_COLOR};
    }
  }
`;

const ProfileHeader = styled.div`
  height: 112px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: ${THEME_COLOR_GRAY};
  border-bottom: 1px solid #dcdcdc;
  padding: 30px 20px;
  padding-left: 110px;
  position: relative;
  strong {
    font-weight: bold;
  }
  p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  p:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const ProfileHeaderImage = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 1000px;
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  background-size: 100%;
  box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.1) inset;
  overflow: hidden;
  &[title]:after {
    display: block;
    content: attr(title);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  & > div {
    background-size: 100%;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 100;
  }
`;

const LimitBanner = styled.div<{ red: boolean }>`
  display: block;
  clear: both;
  font-weight: normal;
  line-height: 1.6em;
  color: white;
  white-space: nowrap;
  padding: 2px 8px;
  text-align: center;
  background: ${(p) =>
    p.red ? `linear-gradient(to right, #fe2260, #fe82a8)` : `${THEME_COLOR}`};
`;

const Badge = styled.div`
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  color: white;
  background: ${ERROR_COLOR};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 22px;
  height: 22px;
  border-radius: 100px;
  overflow: hidden;
`;
